import { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../../hooks/useAuth'
import { useCompany } from '../../../hooks/useCompany'
import { useEarlySignalsConfig } from '../../../hooks/useEarlySignalsConfig'
import { getDownloadUrl } from '../../../hooks/useSupplierSpecialReports'
import { supabase } from '../../../services/supabase'
import { fetchRaSupplierRoster } from '../../../services/supplierRosterCache'
import type { Supplier, SupplierSpecialReport } from '../../../types'
import { DbTableChatCard } from '../DbTableChatCard'
import { RiskBandChart } from '../RiskCharts'
import { EsCard } from '../ui/EsCard'
import { EsPageHeader } from '../ui/EsPageHeader'
import { formatSpend } from '../spendCubeApi'
import { extractSupplierNameCandidate, scoreBandWord } from '../supplierRiskRatingsTable'
import {
  bandCountsUnique,
  collapseSuppliersByName,
  type CollapsedSupplierRisk,
} from '../supplierRollup'
import {
  tryAnswerEarlySignalsFromDb,
  type EarlySignalsDbAnswer,
} from '../tryAnswerEarlySignalsFromDb'
import { cubeSpendForBrief, type BriefCubeSpend } from './briefCubeSpend'
import { bandBarClass, buildBriefPicture } from './briefPicture'
import { excerptFromDocx } from './briefDocx'
import {
  briefKey,
  buildBriefCatalog,
  suppliersForBrief,
  type BriefCatalogItem,
  type BriefPortfolio,
} from './briefCatalog'
import { loadCompanySources, type CompanySources } from './companySources'
import {
  answerText,
  enabledSupplierIds,
  ensureBrief,
  insertTurn,
  listTurns,
  replaceTurnAnswer,
  type BriefTurn,
} from './briefsApi'

type AlertRow = { id: string; supplier_id: string | null; headline: string; published_at: string | null }
type TaskRow = { id: string; supplier_id: string; task: string; status: string }

function portfolioLabel(portfolio: BriefPortfolio): string {
  return portfolio === 'indirect' ? 'Indirect' : 'Direct'
}

function bandLabel(score: number | null): string {
  if (score == null) return 'Unscored'
  const word = scoreBandWord(score)
  return `${score.toFixed(1)} ${word}`
}

function routedQuestion(question: string, category: string): string {
  if (question.toLowerCase().includes(category.toLowerCase())) return question
  if (extractSupplierNameCandidate(question)) return question
  if (/\bsupplier\s+\d+\b/i.test(question)) return question
  return `${question} in ${category}`
}

export function BriefsView() {
  const { user, effectiveCompanyId, loading: authLoading } = useAuth()
  const { settings, loading: companySettingsLoading } = useCompany()
  const { isTabEnabled } = useEarlySignalsConfig()
  const companyPending = authLoading || (!!user && !effectiveCompanyId)
  const briefsSettingsReady =
    !companyPending &&
    !companySettingsLoading &&
    !!effectiveCompanyId &&
    settings?.company_id === effectiveCompanyId
  const allowed = briefsSettingsReady && isTabEnabled('briefs')
  const userId = user?.id ?? null
  const seenCompany = useRef<string | null>(null)
  const [params, setParams] = useSearchParams()
  const category = params.get('category') || ''
  const portfolioRaw = params.get('portfolio')
  const portfolio: BriefPortfolio | null =
    portfolioRaw === 'direct' || portfolioRaw === 'indirect' ? portfolioRaw : null

  const [roster, setRoster] = useState<Supplier[] | null>(null)
  const [rosterError, setRosterError] = useState<string | null>(null)
  const [alerts, setAlerts] = useState<AlertRow[]>([])
  const [tasks, setTasks] = useState<TaskRow[]>([])
  const [reports, setReports] = useState<SupplierSpecialReport[]>([])
  const [turns, setTurns] = useState<BriefTurn[]>([])
  const [liveAnswers, setLiveAnswers] = useState<Record<string, EarlySignalsDbAnswer | null>>({})
  const [turnsFor, setTurnsFor] = useState<string | null>(null)
  const [briefId, setBriefId] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [asking, setAsking] = useState(false)
  const [draft, setDraft] = useState('')
  const [checkingSaved, setCheckingSaved] = useState(false)
  const [excerpt, setExcerpt] = useState<{
    id: string
    name: string
    band: string
    text: string
  } | null>(null)
  const [excerptError, setExcerptError] = useState<string | null>(null)
  const [sources, setSources] = useState<CompanySources | null>(null)
  const [cubeSpend, setCubeSpend] = useState<BriefCubeSpend | null>(null)
  const [cubeFor, setCubeFor] = useState<string | null>(null)
  const [companyHasReports, setCompanyHasReports] = useState(false)

  useEffect(() => {
    if (!effectiveCompanyId) return
    if (seenCompany.current && seenCompany.current !== effectiveCompanyId) {
      setParams({})
      setRoster(null)
    }
    seenCompany.current = effectiveCompanyId
    setCompanyHasReports(false)
    let cancel = false
    Promise.all([
      enabledSupplierIds(effectiveCompanyId),
      fetchRaSupplierRoster(),
      supabase.from('supplier_special_reports').select('supplier_id'),
    ])
      .then(([enabled, rows, reportRes]) => {
        if (cancel) return
        setRoster(rows.filter((row) => row.id && enabled.has(row.id)))
        setCompanyHasReports(
          (reportRes.data ?? []).some((row) => row.supplier_id && enabled.has(row.supplier_id))
        )
      })
      .catch((e: unknown) => {
        if (!cancel) setRosterError(e instanceof Error ? e.message : 'Could not load suppliers.')
      })
    return () => {
      cancel = true
    }
  }, [effectiveCompanyId, setParams])

  useEffect(() => {
    if (!allowed || !effectiveCompanyId) {
      setSources(null)
      return
    }
    let cancel = false
    loadCompanySources(effectiveCompanyId)
      .then((next) => {
        if (!cancel) setSources(next)
      })
      .catch(() => {
        if (!cancel) {
          setSources({ hasNews: false, hasMitigations: false, hasReports: false, hasSpend: false })
        }
      })
    return () => {
      cancel = true
    }
  }, [allowed, effectiveCompanyId])

  const catalog = useMemo(() => (roster ? buildBriefCatalog(roster) : []), [roster])
  const selected = catalog.find((item) => item.category === category && item.portfolio === portfolio) || null
  const rows = useMemo(
    () => (roster && selected ? suppliersForBrief(roster, selected.category, selected.portfolio) : []),
    [roster, selected]
  )
  const collapsed = useMemo(() => collapseSuppliersByName(rows), [rows])
  const supplierIds = useMemo(() => rows.map((s) => s.id), [rows])
  const picture = useMemo(
    () =>
      selected
        ? buildBriefPicture({
            collapsed,
            rows,
            parents: cubeFor === (selected ? briefKey(selected) : '') ? (cubeSpend?.parents ?? []) : [],
            byYear: cubeFor === (selected ? briefKey(selected) : '') ? (cubeSpend?.byYear ?? []) : [],
            supplierCount: selected.supplierCount,
          })
        : null,
    [selected, collapsed, rows, cubeSpend, cubeFor]
  )

  useEffect(() => {
    if (!allowed || !selected || supplierIds.length === 0) {
      setAlerts([])
      setTasks([])
      setReports([])
      return
    }
    let cancel = false
    const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
    const idSet = new Set(supplierIds)
    Promise.all([
      supabase
        .from('supplier_news')
        .select('id, supplier_id, headline, published_at')
        .gte('published_at', since)
        .order('published_at', { ascending: false })
        .limit(400),
      supabase
        .from('supplier_mitigation_tasks')
        .select('id, supplier_id, task, status')
        .in('status', ['open', 'in_progress', 'at_risk', 'scheduled', 'on_hold'])
        .limit(800),
      supabase
        .from('supplier_special_reports')
        .select('id, supplier_id, file_name, file_path, file_size, content_type, created_at')
        .order('created_at', { ascending: false }),
    ]).then(([newsRes, taskRes, reportRes]) => {
      if (cancel) return
      setAlerts(
        ((newsRes.data ?? []) as AlertRow[]).filter((row) => row.supplier_id && idSet.has(row.supplier_id))
      )
      setTasks(((taskRes.data ?? []) as TaskRow[]).filter((row) => idSet.has(row.supplier_id)))
      setReports(
        ((reportRes.data ?? []) as SupplierSpecialReport[]).filter((row) => idSet.has(row.supplier_id))
      )
    })
    return () => {
      cancel = true
    }
  }, [allowed, selected, supplierIds])

  useEffect(() => {
    const spendKey = selected ? briefKey(selected) : ''
    if (!allowed || !effectiveCompanyId || !sources?.hasSpend || rows.length === 0) {
      setCubeSpend(null)
      setCubeFor(null)
      return
    }
    let cancel = false
    setCubeSpend(null)
    setCubeFor(null)
    cubeSpendForBrief(effectiveCompanyId, rows)
      .then((next) => {
        if (cancel) return
        setCubeSpend(next)
        setCubeFor(spendKey)
      })
      .catch(() => {
        if (cancel) return
        setCubeSpend({ parents: [], byYear: [] })
        setCubeFor(spendKey)
      })
    return () => {
      cancel = true
    }
  }, [allowed, effectiveCompanyId, sources?.hasSpend, rows])

  useEffect(() => {
    const turnKey = selected ? briefKey(selected) : ''
    if (!effectiveCompanyId || !userId || !selected) {
      setBriefId(null)
      setTurns([])
      setTurnsFor(null)
      return
    }
    const companyId = effectiveCompanyId
    let cancel = false
    setSaveError(null)
    setCheckingSaved(true)
    ;(async () => {
      try {
        const brief = await ensureBrief(userId, companyId, selected.category, selected.portfolio)
        if (cancel) return
        const saved = await listTurns(brief.id)
        const refreshed: BriefTurn[] = []
        const live: Record<string, EarlySignalsDbAnswer | null> = {}
        const scope = {
          defaultProcurementType: selected.portfolio,
          companyId,
          allowedSupplierIds: new Set(supplierIds),
          categoryScope: selected.category,
        }
        for (const turn of saved) {
          const routed = routedQuestion(turn.question, selected.category)
          const answered = await tryAnswerEarlySignalsFromDb(routed, scope)
          const next = answerText(answered)
          live[turn.id] = answered
          if (next !== turn.answer || routed !== turn.routed_question) {
            await replaceTurnAnswer(turn.id, next, routed)
            refreshed.push({
              ...turn,
              answer: next,
              routed_question: routed,
              refreshed_at: new Date().toISOString(),
            })
          } else {
            refreshed.push(turn)
          }
        }
        if (!cancel) {
          setLiveAnswers(live)
          setTurns(refreshed)
          setTurnsFor(turnKey)
          setBriefId(brief.id)
        }
      } catch (e: unknown) {
        if (!cancel) {
          setSaveError(e instanceof Error ? e.message : 'Questions could not be saved.')
        }
      } finally {
        if (!cancel) setCheckingSaved(false)
      }
    })()
    return () => {
      cancel = true
    }
  }, [allowed, userId, selected, effectiveCompanyId, supplierIds])

  async function ask(raw: string) {
    const question = raw.trim()
    if (!question || !selected || !user || !briefId || asking) return
    setAsking(true)
    setSaveError(null)
    const routed = routedQuestion(question, selected.category)
    try {
      const answered = await tryAnswerEarlySignalsFromDb(routed, {
        defaultProcurementType: selected.portfolio,
        companyId: effectiveCompanyId,
        allowedSupplierIds: new Set(supplierIds),
        categoryScope: selected.category,
      })
      const text = answerText(answered)
      const saved = await insertTurn(briefId, user.id, question, routed, text)
      setLiveAnswers((prev) => ({ ...prev, [saved.id]: answered }))
      setTurns((prev) => [...prev, saved])
      setDraft('')
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'That question could not be saved.')
    } finally {
      setAsking(false)
    }
  }

  if (!allowed) {
    return <Navigate to="/early-signals/ai" replace />
  }

  if (rosterError) {
    return <EsPageHeader eyebrow="Briefs" title="Briefs" description={rosterError} />
  }

  if (!roster) {
    return <EsPageHeader eyebrow="Briefs" title="Briefs" description="Loading categories." />
  }

  if (!selected) {
    return <BriefLibrary catalog={catalog} onOpen={openBrief} />
  }

  function openBrief(item: BriefCatalogItem) {
    setExcerpt(null)
    setExcerptError(null)
    setParams({ category: item.category, portfolio: item.portfolio })
  }

  const nameById = new Map<string, string>()
  for (const row of rows) {
    if (row.id && row.name) nameById.set(row.id, row.name)
  }
  if (!picture) return null

  const questionsReady = turnsFor === briefKey(selected) && !!briefId
  const others = catalog.filter((item) => briefKey(item) !== briefKey(selected))
  const namedReports = reports.filter((report) => picture.namedSupplierIds.has(report.supplier_id))
  const otherReportCount = reports.length - namedReports.length
  const starters = [
    { label: 'Highest risk', prompt: `Who has the highest risk in ${selected.category}?` },
    { label: 'Single source', prompt: `Which suppliers are single-source in ${selected.category}?` },
  ]

  return (
    <div>
      <button
        type="button"
        onClick={() => setParams({})}
        className="mb-4 text-sm font-semibold text-es-navy underline"
      >
        All briefs
      </button>
      <EsCard>
        <p className="text-xs uppercase tracking-wide text-es-muted">{portfolioLabel(selected.portfolio)}</p>
        <h1 className="mt-1 text-lg font-semibold text-es-navy">{selected.category}</h1>
        <p className="mt-2 text-sm text-es-body">
          {selected.supplierCount} suppliers · {selected.high} high risk
        </p>
        {others.length > 0 ? (
          <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((item) => (
              <li key={briefKey(item)}>
                <BriefTile item={item} onOpen={openBrief} compact />
              </li>
            ))}
          </ul>
        ) : null}

        <div className="mt-8">
          <RiskBandChart embedded title="Risk bands" bands={bandCountsUnique(collapsed)} />
        </div>

        {sources?.hasSpend ? (
          <section className="mt-8">
            <h2 className="mb-2 text-lg font-semibold text-es-navy">Who holds the spend</h2>
            {cubeFor !== briefKey(selected) || cubeSpend == null ? (
              <p className="text-sm text-es-muted">Loading this company’s spend file.</p>
            ) : picture.spendBars.length === 0 ? (
              <p className="text-sm text-es-body">
                No spend on this company’s file for suppliers in this category.
              </p>
            ) : (
              <>
                <MetricBars
                  rows={picture.spendBars.map((bar) => ({
                    key: bar.key,
                    label: bar.label,
                    value: bar.spend,
                    valueLabel: `${formatSpend(bar.spend)} · ${bar.sharePct}%`,
                    barClass: bar.rest ? 'bg-es-navy/30' : bandBarClass(bar.band),
                    mark: bar.singleSource ? 'Single source' : undefined,
                  }))}
                />
                {picture.concentrationCaption ? (
                  <p className="mt-3 text-sm text-es-body">{picture.concentrationCaption}</p>
                ) : null}
              </>
            )}
          </section>
        ) : null}

        <section className="mt-8">
          <h2 className="mb-2 text-lg font-semibold text-es-navy">What is driving the high scores</h2>
          {picture.drivingNote ? (
            <p className="text-sm text-es-body">{picture.drivingNote}</p>
          ) : (
            <MetricBars
              rows={picture.driving.map((bar) => ({
                key: bar.label,
                label: bar.label,
                value: bar.count,
                valueLabel: String(bar.count),
                barClass: 'bg-es-riskHigh',
              }))}
            />
          )}
        </section>

        {sources?.hasSpend && picture.years ? (
          <section className="mt-8">
            <h2 className="mb-2 text-lg font-semibold text-es-navy">This year against last year</h2>
            <MetricBars
              rows={picture.years.map((bar, index) => ({
                key: String(bar.year),
                label: String(bar.year),
                value: bar.spend,
                valueLabel: formatSpend(bar.spend),
                barClass: index === picture.years!.length - 1 ? 'bg-es-navy' : 'bg-es-muted/70',
              }))}
            />
          </section>
        ) : null}

        {sources?.hasSpend && cubeFor === briefKey(selected) && cubeSpend && picture.countries.length === 1 ? (
          <p className="mt-8 text-sm text-es-body">All of this spend sits in {picture.countries[0].label}.</p>
        ) : null}
        {sources?.hasSpend && cubeFor === briefKey(selected) && cubeSpend && picture.countries.length > 1 ? (
          <section className="mt-8">
            <h2 className="mb-2 text-lg font-semibold text-es-navy">Where it sits</h2>
            <MetricBars
              rows={picture.countries.map((bar) => ({
                key: bar.key,
                label: bar.label,
                value: bar.spend,
                valueLabel: formatSpend(bar.spend),
                barClass: bar.key === '__rest_country__' ? 'bg-es-navy/30' : 'bg-es-signal',
              }))}
            />
          </section>
        ) : null}

        {sources?.hasNews ? (
          <section className="mt-8">
            <h2 className="mb-2 text-lg font-semibold text-es-navy">What changed in the last 7 days</h2>
            {alerts.length === 0 ? (
              <p className="text-sm text-es-body">No alerts in the past 7 days for this category.</p>
            ) : (
              <ul className="space-y-1 text-sm text-es-body">
                {alerts.slice(0, 8).map((alert) => (
                  <li key={alert.id}>
                    {nameById.get(alert.supplier_id || '') || 'Supplier'} · {alert.headline}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}

        {sources?.hasMitigations ? (
          <section className="mt-8">
            <h2 className="mb-2 text-lg font-semibold text-es-navy">Open mitigations</h2>
            {tasks.length === 0 ? (
              <p className="text-sm text-es-body">No open mitigations for this category.</p>
            ) : (
              <ul className="space-y-1 text-sm text-es-body">
                {tasks.slice(0, 8).map((task) => (
                  <li key={task.id}>
                    {nameById.get(task.supplier_id) || 'Supplier'} · {task.task}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}

        {companyHasReports ? (
          <section className="mt-8">
            <h2 className="mb-2 text-lg font-semibold text-es-navy">Special reports</h2>
            <p className="mb-3 text-sm text-es-muted">
              A quote from the file sits next to the live score. The file does not change the band.
            </p>
            {namedReports.length === 0 && otherReportCount === 0 ? (
              <p className="text-sm text-es-body">No special reports for suppliers in this category.</p>
            ) : null}
            {namedReports.length > 0 ? (
              <ul className="space-y-2">
                {namedReports.map((report) => {
                  const supplierName = nameById.get(report.supplier_id) || 'Supplier'
                  const score = scoreForSupplier(collapsed, supplierName)
                  const open = excerpt?.id === report.id
                  return (
                    <li key={report.id} className="text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-es-navy">{report.file_name}</span>
                        <span className="text-es-muted">
                          {supplierName} · {bandLabel(score)}
                        </span>
                        <button
                          type="button"
                          className="text-es-navy underline"
                          onClick={() => downloadReport(report)}
                        >
                          Download
                        </button>
                        <button
                          type="button"
                          className="text-es-navy underline"
                          onClick={() => readReport(report, supplierName, bandLabel(score))}
                        >
                          Read excerpt
                        </button>
                      </div>
                      {open && excerpt ? (
                        <blockquote className="mt-3 whitespace-pre-wrap border-l-2 border-es-navy pl-3 text-sm text-es-body">
                          <p className="mb-1 font-semibold text-es-navy">
                            {excerpt.name} · live score {excerpt.band}
                          </p>
                          {excerpt.text || 'This Word file has no readable text.'}
                        </blockquote>
                      ) : null}
                    </li>
                  )
                })}
              </ul>
            ) : null}
            {otherReportCount > 0 ? (
              <p className="mt-3 text-sm text-es-body">
                {otherReportCount} other {otherReportCount === 1 ? 'report is' : 'reports are'} on suppliers
                outside this view.
              </p>
            ) : null}
            {excerptError ? <p className="mt-3 text-sm text-red-700">{excerptError}</p> : null}
          </section>
        ) : null}

        <section className="mt-8 border-t border-es-hairline pt-6">
          <h2 className="mb-2 text-lg font-semibold text-es-navy">Category analyst</h2>
          {!questionsReady ? (
            <p className="mb-3 text-sm text-es-muted">
              {saveError ? saveError : 'Opening this brief on your account.'}
            </p>
          ) : null}
          {questionsReady && turns.length === 0 && !checkingSaved ? (
            <p className="mb-3 text-sm text-es-body">Ask about the suppliers in this category.</p>
          ) : null}
          {questionsReady && turns.length > 0 ? (
            <ul className="mb-4 space-y-4">
              {turns.map((turn) => (
                <li key={turn.id}>
                  <p className="text-sm font-semibold text-es-navy">{turn.question}</p>
                  <div className="mt-2">
                    <AnalystAnswer live={liveAnswers[turn.id]} fallback={turn.answer} onAsk={(q) => void ask(q)} asking={asking || !questionsReady} />
                  </div>
                  {turn.refreshed_at ? (
                    <p className="mt-1 text-xs text-es-muted">Updated from current data.</p>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          <form
            className="relative"
            onSubmit={(event) => {
              event.preventDefault()
              void ask(draft)
            }}
          >
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Ask about this category"
              className="w-full rounded-[var(--radius-lg)] border border-es-hairline bg-es-white py-2.5 pl-4 pr-16 text-sm text-es-body shadow-[var(--shadow-card)] focus:border-es-signal focus:outline-none focus:ring-2 focus:ring-es-signal/20 disabled:opacity-70"
              aria-label="Ask Category analyst"
              disabled={asking || !questionsReady}
            />
            <button
              type="submit"
              disabled={asking || !questionsReady || !draft.trim()}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-[var(--radius-md)] bg-es-signal px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50"
              aria-label="Send"
            >
              {asking ? '…' : 'Send'}
            </button>
          </form>
          <div className="mt-3 flex flex-wrap gap-2">
            {starters.map((starter) => (
              <button
                key={starter.label}
                type="button"
                disabled={asking || !questionsReady}
                onClick={() => void ask(starter.prompt)}
                className="rounded-[var(--radius-md)] border border-es-hairline bg-es-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-es-muted hover:border-es-signal hover:text-es-signal disabled:opacity-50"
              >
                {starter.label}
              </button>
            ))}
          </div>
          {saveError ? <p className="mt-2 text-sm text-red-700">{saveError}</p> : null}
        </section>
      </EsCard>
    </div>
  )

  async function downloadReport(report: SupplierSpecialReport) {
    const url = await getDownloadUrl(report.file_path)
    if (!url) {
      setExcerptError('The file could not be downloaded.')
      return
    }
    const res = await fetch(url)
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = report.file_name
    link.click()
    URL.revokeObjectURL(objectUrl)
  }

  async function readReport(report: SupplierSpecialReport, supplierName: string, band: string) {
    setExcerptError(null)
    setExcerpt(null)
    const url = await getDownloadUrl(report.file_path)
    if (!url) {
      setExcerptError('The file could not be opened.')
      return
    }
    const res = await fetch(url)
    const buf = await res.arrayBuffer()
    const isDocx = report.file_name.toLowerCase().endsWith('.docx')
    const text = isDocx ? await excerptFromDocx(buf) : 'This file is not a Word document, so it was not quoted.'
    setExcerpt({ id: report.id, name: supplierName, band, text })
  }
}

function AnalystAnswer({
  live,
  fallback,
  onAsk,
  asking,
}: {
  live: EarlySignalsDbAnswer | null | undefined
  fallback: string
  onAsk: (question: string) => void
  asking: boolean
}) {
  if (live?.kind === 'table') {
    return <DbTableChatCard table={live.table} onAsk={onAsk} askDisabled={asking} />
  }
  return <p className="whitespace-pre-wrap text-sm text-es-body">{live ? answerText(live) : fallback}</p>
}

function BriefLibrary({
  catalog,
  onOpen,
}: {
  catalog: BriefCatalogItem[]
  onOpen: (item: BriefCatalogItem) => void
}) {
  return (
    <div>
      <EsPageHeader
        eyebrow="Briefs"
        title="Category briefs"
        description="One brief for each category that has suppliers enabled for this company. Questions stay on your account for this company."
      />
      {catalog.length === 0 ? (
        <p className="text-sm text-es-body">No suppliers are enabled for this company.</p>
      ) : null}
      <ul className="grid gap-3 sm:grid-cols-2">
        {catalog.map((item) => (
          <li key={briefKey(item)}>
            <BriefTile item={item} onOpen={onOpen} />
          </li>
        ))}
      </ul>
    </div>
  )
}

function BriefTile({
  item,
  onOpen,
  compact = false,
}: {
  item: BriefCatalogItem
  onOpen: (item: BriefCatalogItem) => void
  compact?: boolean
}) {
  return (
    <button type="button" onClick={() => onOpen(item)} className="w-full text-left">
      <EsCard className={`h-full hover:border-es-navy ${compact ? '!p-3' : ''}`}>
        <p className="text-xs uppercase tracking-wide text-es-muted">{portfolioLabel(item.portfolio)}</p>
        <h2 className={`mt-1 font-semibold text-es-navy ${compact ? 'text-base' : 'text-lg'}`}>{item.category}</h2>
        <p className="mt-2 text-sm text-es-body">
          {item.supplierCount} suppliers · {item.high} high risk
        </p>
      </EsCard>
    </button>
  )
}

function MetricBars({
  rows,
}: {
  rows: { key: string; label: string; value: number; valueLabel: string; barClass: string; mark?: string }[]
}) {
  const max = Math.max(1, ...rows.map((row) => row.value))
  return (
    <ul className="space-y-2.5">
      {rows.map((row) => (
        <li key={row.key}>
          <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
            <span className="min-w-0 truncate font-medium text-es-navy">
              {row.label}
              {row.mark ? <span className="ml-2 font-normal text-es-muted">{row.mark}</span> : null}
            </span>
            <span className="shrink-0 tabular-nums text-es-muted">{row.valueLabel}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-es-haze">
            <div
              className={`h-full rounded-full ${row.barClass}`}
              style={{ width: `${Math.max(row.value > 0 ? 2 : 0, (row.value / max) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  )
}

function scoreForSupplier(collapsed: CollapsedSupplierRisk[], name: string): number | null {
  const key = name.trim().toLowerCase()
  const match = collapsed.find((row) => row.name.trim().toLowerCase() === key)
  return match?.overall_score ?? null
}
